import dayjs from 'dayjs';

/**
 * Extracts wall-clock date/time from an ISO string without applying the timezone offset.
 * Partner deeplinks send e.g. "2026-05-04T10:00:00-07:00" meaning 10:00 at the rental
 * location; the API expects that literal time, not a conversion to the browser timezone.
 *
 * @param {string} dateTime - ISO date string, optionally with offset or Z
 * @returns {string} e.g. "2026-05-04T10:00:00", or "" if input is empty
 */
export const parseWallClockDateTime = (dateTime) => {
    if (dateTime == null || dateTime === '') return '';
    const str = String(dateTime).trim();
    const match = str.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})(?::(\d{2}))?/);
    if (!match) return str;
    const seconds = match[3] ?? '00';
    return `${match[1]}T${match[2]}:${seconds}`;
};

/**
 * Dayjs for form pickers / display from an ISO string, using wall-clock components only.
 * @param {string} dateTime
 * @returns {import('dayjs').Dayjs | null}
 */
export const dayjsFromWallClockIso = (dateTime) => {
    const wallClock = parseWallClockDateTime(dateTime);
    if (!wallClock) return null;
    const parsed = dayjs(wallClock);
    return parsed.isValid() ? parsed : null;
};

/**
 * Parses "HH:mm" (24h) from the time Select into a Dayjs (today’s date is ignored when merging with a pick-up date).
 * Avoids dayjs(str, 'HH:mm') without customParseFormat, which can mis-parse midnight.
 */
export const dayjsFromTimeString = (timeString) => {
    if (timeString == null || timeString === '') return null;
    const m = String(timeString).match(/^(\d{1,2}):(\d{2})$/);
    if (!m) return null;
    const hh = parseInt(m[1], 10);
    const mm = parseInt(m[2], 10);
    if (Number.isNaN(hh) || Number.isNaN(mm) || hh > 23 || mm > 59) return null;
    return dayjs().hour(hh).minute(mm).second(0).millisecond(0);
};

/**
 * Read rental days from API/search payload (supports camelCase and PascalCase).
 * @param {Object} source
 * @returns {number|null}
 */
export const getRentalDays = (source) => {
    if (source == null || typeof source !== 'object') return null;
    const days = source.rentalDays ?? source.RentalDays ?? source.days ?? source.Days;
    if (days == null || days === '') return null;
    const parsed = Number(days);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

/**
 * Format rental days for display (e.g. "for 2 days").
 * @param {number|null|undefined} rentalDays
 * @returns {string}
 */
export const formatRentalDays = (rentalDays) => {
    const days = getRentalDays({ rentalDays });
    if (!days) return '';
    return `for ${days} ${days === 1 ? 'day' : 'days'}`;
};

/**
 * Calculate and format rental duration between two dates
 * @param {string} fromDateTime - ISO date string
 * @param {string} toDateTime - ISO date string
 * @returns {string} Formatted duration (e.g., "2 days", "3 hours", "45 minutes")
 */
export const calculateRentalDuration = (fromDateTime, toDateTime) => {
    if (!fromDateTime || !toDateTime) {
        return '';
    }

    const from = dayjsFromWallClockIso(fromDateTime);
    const to = dayjsFromWallClockIso(toDateTime);
    
    if (!from.isValid() || !to.isValid()) {
        return '';
    }

    const diffInMs = to.diff(from);
    
    if (diffInMs <= 0) {
        return '';
    }

    // Calculate days, hours, and minutes
    const days = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diffInMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diffInMs % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) {
        return `for ${days} ${days === 1 ? 'day' : 'days'}`;
    } else if (hours > 0) {
        return `for ${hours} ${hours === 1 ? 'hour' : 'hours'}`;
    } else if (minutes > 0) {
        return `for ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
    } else {
        return '';
    }
};

/**
 * Combines a calendar date with a time-of-day for search/booking APIs.
 * Formats in local wall-clock (no UTC shift). Using Date#toISOString() would
 * turn local midnight into e.g. 19:00:00Z for UTC+5, which is wrong for "00:00".
 *
 * @param {import('dayjs').Dayjs | Date | string} datePart - Selected calendar date
 * @param {import('dayjs').Dayjs | Date | string} timePart - Selected time (hour/minute used)
 * @returns {string} e.g. "2026-04-17T00:00:00"
 */
export const combineDateTimeForSearchApi = (datePart, timePart) => {
    if (!datePart || !timePart) return '';
    const t = dayjs(timePart);
    if (!t.isValid()) return '';
    return dayjs(datePart)
        .hour(t.hour())
        .minute(t.minute())
        .second(0)
        .millisecond(0)
        .format('YYYY-MM-DDTHH:mm:ss');
};

export default calculateRentalDuration;

